import {
  Avatar,
  Button,
  Col,
  DatePicker,
  Form,
  Image,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
  Card,
  Typography,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PlusCircleOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
  UploadOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { fetchCities } from "../city/FetchCities";
import { fetchCountries } from "../country/FetchCountries";
import { fetchStates } from "../state/FetchStates";
import { fetchPincode } from "../pincode/FetchPincode";
import { fetchLanguages } from "../language/FetchLanguages";
import { fetchCustomers } from "./FetchCustomers";
import { createStyles } from "antd-style";
import Highlighter from "react-highlight-words";
import FamilyMember from "../familymember/FamilyMember";
import ImageUpload from "../imageUpload/ImageUpload";
import { fetchHealthConditions } from "../healthCondition/FetchHealthCondition";
import CustomerAddress from "../customerAddress/CustomerAddress";
import CustomerProfileView from "../customerProfileView/CustomerProfile View";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { token } from "../../constants/Constants";
import { logout } from "../../redux/slices/userSlice";
import {
  create_customer,
  fetch_coustomers,
  fetch_coustomers_optimized,
  update_customer,
} from "../../endpoints/API";

const { Text } = Typography;

const useStyle = createStyles(({ css, token }) => {
  const { antCls } = token;
  return {
    customTable: css`
      ${antCls}-table {
        ${antCls}-table-container {
          ${antCls}-table-body,
          ${antCls}-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `,
  };
});

// ── Breakpoint hook ──
function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return {
    isMobile: width < 576,
    isTablet: width >= 576 && width < 992,
    isDesktop: width >= 992,
  };
}

const Customer = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const isUser = useSelector((state) => state.user.user);
  const { isMobile, isTablet } = useBreakpoint();

  const [isCustomerFormVisible, setIsCustomerFormVisible] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [form] = Form.useForm();
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const { styles } = useStyle();
  const [isFamilyMemberModalVisible, setIsFamilyMemberModalVisible] =
    useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [isCustomerDetail, setIsCustomerDetail] = useState("");
  const [isCustomerAddressModal, setIsCustomerAddressModal] = useState(false);

  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [selectedCustomerProfile, setSelectedCustomerProfile] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  // ── Profile modal ──
  const openProfileModal = (customer) => {
    setSelectedCustomerProfile(customer);
    setIsProfileModalVisible(true);
  };

  const handleProfileModalCancel = () => {
    setIsProfileModalVisible(false);
  };

  // ── Address modal ──
  const openCustomerAddressModal = (customer) => {
    setSelectedCustomerName(
      customer.first_name +
        " " +
        customer.middle_name +
        " " +
        customer.last_name
    );
    setIsCustomerDetail(customer);
    setIsCustomerAddressModal(true);
  };

  const handleCustomerAddressModalCancel = () => {
    setIsCustomerAddressModal(false);
  };

  // ── Family member modal ──
  const openFamilyMemberModal = (customer) => {
    setSelectedCustomerName(
      customer.first_name +
        " " +
        customer.middle_name +
        " " +
        customer.last_name
    );
    setIsCustomerDetail(customer);
    setIsFamilyMemberModalVisible(true);
  };

  const handleFamilyMemberModalCancel = () => {
    setIsFamilyMemberModalVisible(false);
  };

  // ── Data fetching ──
  useEffect(() => {
    const getGlobalData = async () => {
      setIsLoading(true);
      try {
        await loadCustomers(setCustomers, currentPage, searchKeyword, pageSize);
      } catch (error) {
        message.error(
          error?.response?.data?.error || "Failed to fetch customers."
        );
        dispatch(logout());
      } finally {
        setIsLoading(false);
      }
    };

    getGlobalData();
  }, [currentPage, searchKeyword, pageSize]);

  const loadCustomers = async (
    setCustomers,
    page = 1,
    search = "",
    pageSize = 100
  ) => {
    try {
      const response = await fetch_coustomers_optimized(page, search, pageSize);
      if (response.statusText == "OK") {
        const data = response?.data;
        setCustomers(data);
      } else {
        message.error("Failed to fetch customers.");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          "An error occurred while fetching customers."
      );
    }
  };

  // ── Search helpers ──
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleServerSearch = (value) => {
    setSearchText(value);
    setSearchKeyword(value);
    setCurrentPage(1);
    setSearchedColumn("phone");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleServerSearch(selectedKeys[0] || "")}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
            onClick={() => handleServerSearch(selectedKeys[0] || "")}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              handleServerSearch("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  // ── Form submit ──
  const handleSubmit = async (values) => {
    const created_by = isUser.user_id;
    try {
      if (editingCustomer) {
        await update_customer(editingCustomer.id, { ...values, created_by });
        message.success("Customer updated successfully!");
      } else {
        await create_customer({ ...values, created_by });
        message.success("Customer created successfully!");
      }
      setIsCustomerFormVisible(false);
      setEditingCustomer(null);
      form.resetFields();
      await loadCustomers(setCustomers, currentPage, searchKeyword, pageSize);
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          "An error occurred while processing the request."
      );
    }
  };

  const openEditForm = (customer) => {
    setEditingCustomer(customer);
    form.setFieldsValue({
      first_name: customer.first_name,
      middle_name: customer.middle_name,
      last_name: customer.last_name,
      email: customer.email,
      phone: customer.phone,
    });
    setIsCustomerFormVisible(true);
  };

  // ── Customer form (unchanged logic, improved layout) ──
  const renderCustomerForm = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: isMobile ? "auto" : "100vh",
          padding: isMobile ? "0" : "24px 0",
        }}
      >
        <div style={{ width: "100%", maxWidth: 600 }}>
          <Form layout="vertical" form={form} onFinish={handleSubmit}>
            <div className="text-md font-semibold mb-[1rem] text-center">
              {editingCustomer ? "Update Customer" : "Create Customer"}
            </div>

            <div className="p-2 rounded-lg mb-[1rem] shadow-md">
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[
                  { required: true, message: "Please enter first name" },
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>

              <Form.Item label="Middle Name" name="middle_name">
                <Input placeholder="Enter middle name" />
              </Form.Item>

              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[
                  { required: true, message: "Please enter last name" },
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>

              <Form.Item label="Email" name="email">
                <Input placeholder="Enter email" />
              </Form.Item>

              <Form.Item
                label="Phone"
                name="phone"
                rules={[
                  { required: true, message: "Please enter phone number" },
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </div>

            {/* Submission Buttons */}
            <Form.Item
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 0,
              }}
            >
              <Space wrap>
                <Button type="primary" htmlType="submit">
                  {editingCustomer ? "Update" : "Create"}
                </Button>
                <Button onClick={() => setIsCustomerFormVisible(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </div>
    );
  };

  // ── Mobile customer card ──
  const CustomerCard = ({ customer }) => (
    <Card
      size="small"
      style={{ marginBottom: 12, borderRadius: 8 }}
      title={
        <span
          style={{ color: "#1677ff", cursor: "pointer", fontSize: 13 }}
          onClick={() => openProfileModal(customer)}
        >
          {customer.first_name}{" "}
          {customer.middle_name && `${customer.middle_name} `}
          {customer.last_name}
        </span>
      }
      extra={
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => openEditForm(customer)}
        />
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {/* Phone */}
        <div style={{ display: "flex", gap: 8 }}>
          <Text type="secondary" style={{ fontSize: 12, minWidth: 55 }}>
            Phone:
          </Text>
          <Text style={{ fontSize: 12 }}>
            {searchedColumn === "phone" && searchText ? (
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={customer.phone ? customer.phone.toString() : ""}
              />
            ) : (
              customer.phone || "-"
            )}
          </Text>
        </div>

        {/* Email */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Text type="secondary" style={{ fontSize: 12, minWidth: 55 }}>
            Email:
          </Text>
          <Text style={{ fontSize: 12, wordBreak: "break-all" }}>
            {customer.email || "-"}
          </Text>
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginTop: 4,
            paddingTop: 6,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button
            type="text"
            size="small"
            onClick={() => openFamilyMemberModal(customer)}
            style={{ padding: "0 4px", fontSize: 12 }}
          >
            Family <PlusCircleOutlined />
          </Button>
          <Button
            type="text"
            size="small"
            onClick={() => openCustomerAddressModal(customer)}
            style={{ padding: "0 4px", fontSize: 12 }}
          >
            Address <PlusCircleOutlined />
          </Button>
        </div>
      </div>
    </Card>
  );

  // ── Desktop / Tablet columns (all original columns preserved) ──
  const columns = [
    {
      title: "Customers",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      render: (text, customer) => (
        <span
          style={{ color: "#1677ff", cursor: "pointer" }}
          onClick={() => openProfileModal(customer)}
        >
          {customer.first_name}{" "}
          {customer.middle_name && `${customer.middle_name} `}
          {customer.last_name}
        </span>
      ),
    },
    {
      title: "Contact",
      dataIndex: "phone",
      key: "phone",
      fixed: "left",
      ...getColumnSearchProps("phone"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Family Member",
      dataIndex: "family",
      key: "family",
      render: (text, customer) => (
        <Button type="text" onClick={() => openFamilyMemberModal(customer)}>
          Add Member <PlusCircleOutlined />
        </Button>
      ),
    },
    {
      title: "Address",
      dataIndex: "location",
      key: "location",
      render: (text, customer) => (
        <Button type="text" onClick={() => openCustomerAddressModal(customer)}>
          Add Address <PlusCircleOutlined />
        </Button>
      ),
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      fixed: "right",
      render: (_, customer) => (
        <Button onClick={() => openEditForm(customer)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  return (
    <Spin spinning={isLoading} tip="Loading...">
      {/* ── Header row ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        {/* Mobile search bar */}
        {isMobile && !isCustomerFormVisible && (
          <Input.Search
            placeholder="Search by phone or email"
            allowClear
            onSearch={(val) => handleServerSearch(val)}
            onChange={(e) => {
              if (!e.target.value) handleServerSearch("");
            }}
            style={{ flex: 1, minWidth: 0 }}
          />
        )}
        <Button
          type="primary"
          onClick={() => {
            setEditingCustomer(null);
            form.resetFields();
            setIsCustomerFormVisible(true);
          }}
          style={{ marginLeft: "auto" }}
        >
          Create Customer <PlusCircleOutlined />
        </Button>
      </div>

      {/* ── Form or Table/Cards ── */}
      {isCustomerFormVisible ? (
        renderCustomerForm()
      ) : isMobile ? (
        // Mobile card list
        <div>
          {!customers?.results || customers.results.length === 0 ? (
            <div style={{ textAlign: "center", padding: 32, color: "#999" }}>
              No customers found.
            </div>
          ) : (
            customers.results.map((customer) => (
              <CustomerCard
                key={customer.id || customer.phone}
                customer={customer}
              />
            ))
          )}
          {/* Mobile pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              marginTop: 12,
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Text style={{ fontSize: 13 }}>
              Page {currentPage} /{" "}
              {Math.ceil((customers?.count || 0) / pageSize) || 1}
            </Text>
            <Button
              size="small"
              disabled={
                currentPage >= Math.ceil((customers?.count || 0) / pageSize)
              }
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : (
        // Tablet / Desktop table
        <div style={{ overflowX: "auto" }}>
          <Table
            className={styles.customTable}
            dataSource={customers?.results}
            columns={columns}
            scroll={{ x: isTablet ? 700 : true }}
            size={isTablet ? "small" : "middle"}
            pagination={{
              current: currentPage,
              total: customers?.count,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: [10, 50, 100, 200, 500],
              responsive: true,
              onChange: (page) => {
                setCurrentPage(page);
              },
              onShowSizeChange: (page, newSize) => {
                setPageSize(newSize);
                setCurrentPage(1);
              },
            }}
          />
        </div>
      )}

      {/* ── Family Member Modal ── */}
      <Modal
        title={`Add Family Member for ${selectedCustomerName}`}
        open={isFamilyMemberModalVisible}
        onCancel={handleFamilyMemberModalCancel}
        footer={false}
        width={isMobile ? "95vw" : isTablet ? "90vw" : 800}
        centered={isMobile}
        destroyOnClose
      >
        <FamilyMember
          isCustomerDetail={isCustomerDetail}
          handleFamilyMemberModalCancel={handleFamilyMemberModalCancel}
          fetchCustomers={loadCustomers}
        />
      </Modal>

      {/* ── Customer Address Modal ── */}
      <Modal
        title={`Add Address for ${selectedCustomerName}`}
        open={isCustomerAddressModal}
        onCancel={handleCustomerAddressModalCancel}
        footer={false}
        width={isMobile ? "95vw" : isTablet ? "90vw" : 800}
        centered={isMobile}
        destroyOnClose
      >
        <CustomerAddress
          isCustomerDetail={isCustomerDetail}
          handleCustomerAddressModalCancel={handleCustomerAddressModalCancel}
          fetchCustomers={loadCustomers}
        />
      </Modal>

      {/* ── Customer Profile Modal ── */}
      <Modal
        open={isProfileModalVisible}
        onCancel={handleProfileModalCancel}
        footer={false}
        width={isMobile ? "95vw" : isTablet ? "90vw" : 800}
        centered={isMobile}
        destroyOnClose
      >
        <div>
          <CustomerProfileView
            selectedCustomerProfile={selectedCustomerProfile}
          />
        </div>
      </Modal>
    </Spin>
  );
};

export default Customer;